import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { GOOGLE_MAPS_CONFIG, MAP_OPTIONS } from '../../../constants/maps';

type Map = google.maps.Map & { zoom: number };

type LocationMapProps = {
  address: {
    street: string;
    number: string;
    zipcode: string;
    city: string;
    country: string;
  };
  location?: string;
};

const LocationMap = ({ address, location }: LocationMapProps) => {
  const { t } = useTranslation(['jobs']);
  const { isLoaded: isMapLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);
  const { street, number, zipcode, city, country } = address;
  const locationAddress = `${street} ${number}, ${zipcode} ${city}, ${country}`;

  const mapRef = useRef<Map>();
  const [position, setPosition] = useState<google.maps.LatLngLiteral | null>(null);

  const encodedAddress = encodeURIComponent(locationAddress);
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map as Map;
  };

  useEffect(() => {
    if (isMapLoaded) {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address: locationAddress }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;

          setPosition({
            lat: location.lat(),
            lng: location.lng(),
          });
        }
      });
    }
  }, [isMapLoaded, locationAddress]);

  if (!isMapLoaded) {
    return null;
  }

  return (
    <div className="relative aspect-square w-full">
      <a className="text-brand mb-1 block" href={googleMapsUrl} rel="noopener noreferrer" target="_blank">
        {t('routeDescription')}
      </a>
      <a
        className="text-s hover:text-brand absolute left-1 top-8 z-10 m-2 bg-white px-2 py-1"
        href={
          location
            ? `https://www.google.com/maps/search/${encodeURIComponent(location)}`
            : `https://www.google.com/maps/place/${encodedAddress}`
        }
        rel="noopener noreferrer"
        target="_blank"
      >
        {location && <div>{location}</div>}
        <div>{t('viewLargerMap')}</div>
      </a>
      <GoogleMap
        center={position || { lat: 0, lng: 0 }}
        clickableIcons={false}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        onLoad={handleMapLoad}
        options={{ ...MAP_OPTIONS, zoomControl: true }}
        zoom={15}
      >
        {position && <Marker position={position} />}
      </GoogleMap>
      <address className="mt-2 not-italic">
        {location && <span className="text-brand font-bold">{location}</span>}
        <br />
        {street} {number}, {zipcode} {city}, {country}
      </address>
    </div>
  );
};

export default LocationMap;
